import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCw, Loader2, Copy, Check, AlertTriangle, ChevronRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui/button'
import { ExtractionPhaseStepper } from '@/components/extraction/ExtractionPhaseStepper'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { OverviewCitationChip } from '@/components/extraction/OverviewCitationChip'
import { useTenderOverviewStore } from '@/stores/tender-overview-store'
import { useDocumentsStore } from '@/stores/documents-store'
import { remarkCitations, normalizeCitationMarkdown } from '@/lib/remark-citations'
import { buildOverviewClipboard } from '@/lib/tender-overview-copy'
import {
  KEY_FACT_ANCHORS,
  KEY_FACT_FIELDS,
  KEY_FACT_LABELS,
  SECTION_LABELS,
  TENDER_OVERVIEW_SECTION_KEYS,
  type KeyFactField,
  type TenderOverview,
  type TenderOverviewSection,
  type TenderOverviewSectionKey,
} from '@/types/tender-overview'

// ===================
// Constants
// ===================

/** Sections that end with a link-out to a related Analysis sub-tab. */
const SECTION_LINKOUTS: Partial<Record<TenderOverviewSectionKey, { view: string; label: string }>> = {
  red_flags: { view: 'risk_register', label: 'View full Risk Register' },
  respond_to: { view: 'submission_checklist', label: 'View Submission Checklist' },
}

// ===================
// Component
// ===================

interface TenderOverviewViewProps {
  bidId: string
  readOnly: boolean
}

export function TenderOverviewView({ bidId, readOnly }: TenderOverviewViewProps) {
  const overview = useTenderOverviewStore((s) => s.overview)
  const status = useTenderOverviewStore((s) => s.status)
  const phase = useTenderOverviewStore((s) => s.phase)
  const error = useTenderOverviewStore((s) => s.error)
  const load = useTenderOverviewStore((s) => s.load)
  const generate = useTenderOverviewStore((s) => s.generate)
  const documents = useDocumentsStore((s) => s.documents)
  const hasReadyDocuments = useDocumentsStore((s) => s.hasProcessedDocuments())

  /**
   * Generation is gated on documents being ready for analysis, like every
   * sibling extract button on this screen (F34). Ungated, it dispatched a real
   * retrieval pipeline against a document-less bid and failed with a message
   * naming none of that — and Try again reproduced it forever.
   *
   * The reason is rendered as visible copy rather than a `title` tooltip: the
   * Button base sets `disabled:pointer-events-none`, so a tooltip on a disabled
   * button can never fire (F33).
   */
  const blockedReason = hasReadyDocuments
    ? null
    : documents.length === 0
      ? 'Upload the tender documents first — there is nothing to summarise yet.'
      : 'Your documents are still being processed. You can generate the summary once they are ready.'

  useEffect(() => {
    void load(bidId)
  }, [bidId, load])

  const staleDocCount = useMemo(() => {
    if (!overview) return 0
    return documents.filter((d) => d.createdAt > overview.generatedAt).length
  }, [documents, overview])

  // ── Generating ──
  // Same stepper the item-producing Analysis tabs use — the summary lane emits
  // the identical five phases, so the loading UI is shared rather than forked.
  if (status === 'generating') {
    return <ExtractionPhaseStepper category="tender_overview" phase={phase} />
  }

  // ── Loading (first visit, before any overview hydrates) ──
  if (status === 'loading' && !overview) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--ai-accent-text))]" />
      </div>
    )
  }

  // ── Error (no prior overview to fall back on) ──
  if (status === 'error' && !overview) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertTriangle className="h-10 w-10 text-destructive mb-3" />
        <p className="text-sm font-medium">Generation failed</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          {error ?? 'Something went wrong generating the tender summary.'}
        </p>
        {/* Why Try again is unavailable. Stated here rather than as a `title`
            on the disabled button, which could never render (F33). */}
        {blockedReason && (
          <p className="text-sm text-muted-foreground mt-2 max-w-md">{blockedReason}</p>
        )}
        {!readOnly && (
          <Button
            variant="outline"
            className="gap-2 mt-4"
            disabled={blockedReason !== null}
            onClick={() => void generate(bidId)}
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        )}
      </div>
    )
  }

  // ── Empty ──
  if (!overview) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <GradientSparkleIcon className="h-10 w-10 mb-3" />
        <p className="text-lg font-semibold">No Tender Summary yet</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          {blockedReason ??
            'Generate a concise, cited overview of this tender — the key facts, scope, commercials, risks and what you must respond to.'}
        </p>
        {!readOnly && (
          <Button
            className="gap-2 mt-4"
            disabled={blockedReason !== null}
            onClick={() => void generate(bidId)}
          >
            <GradientSparkleIcon className="h-4 w-4" />
            Generate Tender Summary
          </Button>
        )}
      </div>
    )
  }

  // ── Populated ──
  return (
    <div>
      <SummaryHeader overview={overview} readOnly={readOnly} onRegenerate={() => void generate(bidId)} />

      {status === 'error' && (
        <div className="flex items-center justify-between gap-3 mb-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
            <p className="truncate text-destructive">
              Generation failed{error ? ` — ${error}` : ''}
            </p>
          </div>
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={() => void generate(bidId)}
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
          )}
        </div>
      )}

      {staleDocCount > 0 && (
        <p className="mt-1 mb-4 text-meta">
          {staleDocCount} document{staleDocCount !== 1 ? 's' : ''} uploaded
          since this summary was generated.
          {!readOnly && (
            <button
              type="button"
              onClick={() => void generate(bidId)}
              className="ml-1 text-[hsl(var(--link))] hover:underline"
            >
              Regenerate
            </button>
          )}
        </p>
      )}

      <KeyFacts keyFacts={overview.keyFacts} />

      {TENDER_OVERVIEW_SECTION_KEYS.map((key) => {
        const section = overview.sections.find((s) => s.key === key)
        if (!section) return null
        return <SectionBlock key={key} section={section} />
      })}
    </div>
  )
}

// ===================
// Header
// ===================

function SummaryHeader({
  overview,
  readOnly,
  onRegenerate,
}: {
  overview: TenderOverview
  readOnly: boolean
  onRegenerate: () => void
}) {
  const [copied, setCopied] = useState(false)

  const generatedAt = new Date(overview.generatedAt).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  const handleCopy = async () => {
    const { plain, html } = buildOverviewClipboard(overview)
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ])
    } catch {
      // Rich write unsupported (older browsers, missing ClipboardItem) — fall
      // back to plain text so the copy still succeeds.
      try {
        await navigator.clipboard.writeText(plain)
      } catch {
        return
      }
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-sm font-medium">Tender Summary</h2>
        <p className="text-meta mt-0.5">Generated {generatedAt}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => void handleCopy()}>
          {copied ? (
            <>
              <Check className="h-4 w-4 mr-1.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1.5" />
              Copy
            </>
          )}
        </Button>
        {!readOnly && (
          <Button variant="ghost" size="sm" onClick={onRegenerate}>
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Regenerate
          </Button>
        )}
      </div>
    </div>
  )
}

// ===================
// Key facts
// ===================

function KeyFacts({ keyFacts }: { keyFacts: TenderOverview['keyFacts'] }) {
  // Fields shown in the grid: anchors always, plus stated non-anchors.
  const gridFields = KEY_FACT_FIELDS.filter(
    (field) => KEY_FACT_ANCHORS.includes(field) || keyFacts[field]?.value != null
  )

  // Non-anchor fields with no value collapse into a single muted line.
  const collapsedFields = KEY_FACT_FIELDS.filter(
    (field) => !KEY_FACT_ANCHORS.includes(field) && keyFacts[field]?.value == null
  )

  return (
    <div className="border-b border-border pb-6 mb-6">
      <h3 className="text-sm font-medium mb-3">Key facts</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
        {gridFields.map((field) => (
          <KeyFactItem key={field} field={field} value={keyFacts[field]} />
        ))}
      </div>
      {collapsedFields.length > 0 && (
        <p className="mt-4 text-xs italic text-muted-foreground">
          Not stated in the documents:{' '}
          {collapsedFields.map((f) => KEY_FACT_LABELS[f].toLowerCase()).join(', ')}
        </p>
      )}
    </div>
  )
}

function KeyFactItem({
  field,
  value,
}: {
  field: KeyFactField
  value: TenderOverview['keyFacts'][KeyFactField]
}) {
  return (
    <div>
      <div className="text-meta mb-0.5">{KEY_FACT_LABELS[field]}</div>
      <div className="text-sm leading-snug">
        {value?.value == null ? (
          <span className="italic text-muted-foreground">Not stated</span>
        ) : (
          <>
            {value.value}
            {value.citation && <OverviewCitationChip citation={value.citation} />}
          </>
        )}
      </div>
    </div>
  )
}

// ===================
// Sections
// ===================

function SectionBlock({ section }: { section: TenderOverviewSection }) {
  const linkout = SECTION_LINKOUTS[section.key]

  // react-markdown's Components type doesn't know about the custom `citation`
  // element produced by remarkCitations — cast as MessageBubble does.
  const components = useMemo(
    () =>
      ({
        citation: ({ index }: { index?: number }) => {
          // Resolve by marker first (citations may be out of positional order),
          // falling back to positional lookup for legacy/unmarked data.
          const citation =
            index != null
              ? (section.citations.find((c) => c.marker === index) ?? section.citations[index - 1])
              : undefined
          if (!citation) return <span>[{index}]</span>
          return <OverviewCitationChip citation={citation} />
        },
      }) as unknown as React.ComponentProps<typeof ReactMarkdown>['components'],
    [section.citations]
  )

  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium mb-2">{SECTION_LABELS[section.key]}</h3>
      {section.notAddressed ? (
        <p className="text-sm italic text-muted-foreground">{section.contentMd}</p>
      ) : (
        <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-2 prose-ul:my-2 prose-li:my-0.5">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkCitations(section.citations.length)]}
            components={components}
          >
            {normalizeCitationMarkdown(section.contentMd)}
          </ReactMarkdown>
        </div>
      )}
      {linkout && (
        <p className="mt-2 text-xs">
          <Link
            to={`?view=${linkout.view}`}
            className="inline-flex items-center gap-0.5 text-[hsl(var(--link))] hover:underline"
          >
            {linkout.label}
            <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
          </Link>
        </p>
      )}
    </div>
  )
}
